// Byråns varumärke i offerten: färg, typsnitt, ton, texter och kontaktperson.
// Layouten är fast, så offerten blir alltid komplett (ADR 0009). Loggan och
// kontrollen av färgens kontrast kommer i steg 7.2 (ADR 0031).
import { z } from 'zod';

const text = (max: number) => z.string().trim().min(1).max(max);

/** En färg som #RRGGBB. */
export const hexColorSchema = z
  .string()
  .regex(/^#[0-9a-fA-F]{6}$/, 'ska vara en färg som #23406B')
  .transform((farg) => farg.toUpperCase());

/** Typsnittsparen i offerten: Modern sans, Klassisk serif och Neutral. */
export const fontPairSchema = z.enum(['sans', 'serif', 'neutral']);
export type FontPair = z.infer<typeof fontPairSchema>;

/** Hur AI skriver inledningen: formell, personlig eller avslappnad. */
export const toneSchema = z.enum(['formal', 'personal', 'relaxed']);
export type Tone = z.infer<typeof toneSchema>;

export const brandingSchema = z.object({
  accentColor: hexColorSchema,
  fontPair: fontPairSchema,
  tone: toneSchema,
  /** Byråns egen beskrivning av tonen, som AI får med sig. */
  toneDescription: text(500).optional(),
  /** Inledande fras före AI:s inledning, till exempel "Hej och tack för förfrågan!". */
  introPhrase: text(200).optional(),
  /** Vad som inte ingår i offerten. */
  notIncluded: text(2000).optional(),
  /** Villkor, till exempel årlig avstämning och löpande taxa. */
  terms: text(2000).optional(),
  /** Sidfot, till exempel auktorisation. */
  footer: text(300).optional(),
  /** Kontaktpersonen som står som avsändare i offerten. */
  contactName: text(100).optional(),
  contactTitle: text(100).optional(),
  contactEmail: z.email().optional(),
  contactPhone: text(30).optional(),
  address: text(200).optional(),
  /** Byråns integritetspolicy, som widgeten länkar till (ADR 0052). */
  privacyUrl: z
    .url({ protocol: /^https?$/ })
    .max(500)
    .optional(),
});

export type Branding = z.infer<typeof brandingSchema>;

/** Varumärket för en byrå som inte har valt något än. */
export const defaultBranding: Branding = {
  accentColor: '#23406B',
  fontPair: 'sans',
  tone: 'personal',
};
